import React from 'react';

export type ButtonVariant = 'primary' | 'secondary';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
};

interface ButtonBaseProps {
    /** Visual style; maps to the existing `btn-primary` / `btn-secondary` classes. */
    variant?: ButtonVariant;
    className?: string;
    children?: React.ReactNode;
}

export type ButtonProps =
    | (ButtonBaseProps & { as: 'a' } & React.AnchorHTMLAttributes<HTMLAnchorElement>)
    | (ButtonBaseProps & { as?: 'button' } & React.ButtonHTMLAttributes<HTMLButtonElement>);

/**
 * Shared call-to-action primitive. Renders an `<a>` when `as="a"` (with the
 * standard anchor props, e.g. `href`), otherwise a native `<button>`.
 * Applies the existing `btn-primary` / `btn-secondary` visual classes so it
 * is a drop-in replacement for the ad-hoc markup used across the template,
 * and automatically picks up the global `:focus-visible` ring and hover
 * polish for keyboard and mouse users alike.
 */
const Button: React.FC<ButtonProps> = (props) => {
    const { variant, className = '', children, as, ...rest } = props;
    const classes = ['btn', variant ? VARIANT_CLASS[variant] : '', className].filter(Boolean).join(' ');

    if (as === 'a') {
        return (
            <a className={classes} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
                {children}
            </a>
        );
    }

    return (
        <button className={classes} {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
            {children}
        </button>
    );
};

export default Button;
